import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { ScanReviewActions } from "@/components/ScanReviewActions";

interface PendingScan {
  id: string;
  title: string;
  category: string | null;
  era: string | null;
  brand: string | null;
  size: string | null;
  condition: string | null;
  condition_notes: string | null;
  material: string | null;
  color: string | null;
  notes: string[] | null;
  tags: string[] | null;
  verdict: string;
  verdict_label: string | null;
  confidence: number | null;
  price_suggested: number | null;
  verification: unknown;
  photos: string[] | null;
  completed_at: string | null;
  created_at: string;
}

/** "To Review" — completed verifications waiting for the user to decide
 *  whether to add them to their closet, post to the feed, or discard. */
export function ReviewQueueSection() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState<PendingScan[]>([]);

  const load = useCallback(async () => {
    if (!user) { setItems([]); return; }
    const { data } = await supabase
      .from("scans")
      .select("id,title,category,era,brand,size,condition,condition_notes,material,color,notes,tags,verdict,verdict_label,confidence,price_suggested,verification,photos,completed_at,created_at")
      .eq("user_id", user.id)
      .eq("processing_status", "complete")
      .eq("review_status", "pending")
      .order("created_at", { ascending: false });
    setItems((data ?? []) as PendingScan[]);
  }, [user]);

  useEffect(() => { void load(); }, [load]);

  if (items.length === 0) return null;

  return (
    <div style={{ padding: "20px 20px 0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
        <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 22, color: "hsl(var(--ink))" }}>
          To Review
        </div>
        <span className="caps">{items.length} pending</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {items.map((s) => {
          const photo = s.photos?.[0] ?? null;
          const isUrl = typeof photo === "string" && /^https?:\/\//.test(photo);
          const verdictLabel = s.verdict_label ?? (s.verdict === "PASS" ? "FIG Verified" : s.verdict);
          return (
            <div key={s.id} style={{
              background: "hsl(var(--cream))", border: "1px solid hsl(var(--ink))",
              padding: 12, display: "flex", flexDirection: "column", gap: 10,
            }}>
              <button
                onClick={() => nav(`/result?scanId=${s.id}`)}
                aria-label={`Open full report for ${s.title}`}
                style={{
                  display: "flex", gap: 12, background: "transparent",
                  border: "none", padding: 0, cursor: "pointer", textAlign: "left",
                  color: "inherit", width: "100%",
                }}
              >
                {isUrl && (
                  <img src={photo} alt={s.title} style={{
                    width: 72, height: 72, objectFit: "cover",
                    border: "1px solid hsl(var(--ink))", flexShrink: 0,
                  }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 15, color: "hsl(var(--ink))" }}>
                    {s.title}
                  </div>
                  <div className="caps" style={{ marginTop: 2, fontSize: 9, color: "hsl(var(--ink-2))" }}>
                    {[s.category, s.era].filter(Boolean).join(" · ") || "Verified"}
                  </div>
                  <div style={{
                    marginTop: 6, display: "inline-block",
                    background: s.verdict === "PASS" ? "hsl(var(--olive))" : "hsl(var(--rust))",
                    color: "hsl(var(--cream))",
                    padding: "2px 8px",
                    fontFamily: "'IBM Plex Mono', monospace", fontSize: 9,
                    letterSpacing: "0.1em", textTransform: "uppercase",
                  }}>
                    {verdictLabel}
                  </div>
                  <div className="caps" style={{ marginTop: 6, fontSize: 9, color: "hsl(var(--ink-2))" }}>
                    Verified {new Date(s.completed_at ?? s.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  </div>
                  <div className="caps" style={{ marginTop: 6, fontSize: 9, color: "hsl(var(--rust))" }}>
                    Tap to open full report ›
                  </div>
                </div>
              </button>
              <ScanReviewActions
                scanId={s.id}
                compact
                postState={{
                  photos: s.photos ?? [],
                  title: s.title,
                  brand: s.brand ?? "",
                  size: s.size ?? "",
                  condition: s.condition ?? "",
                  conditionNotes: s.condition_notes ?? "",
                  era: s.era ?? "",
                  category: s.category ?? "",
                  material: s.material ?? "",
                  color: s.color ?? "",
                  notes: s.notes ?? [],
                  tags: s.tags ?? [],
                  verification: s.verification ?? null,
                  verdict: s.verdict,
                  verdictLabel: s.verdict_label,
                  confidence: s.confidence,
                  priceSuggested: s.price_suggested,
                }}
                onActioned={(kind) => {
                  if (kind !== "feed") {
                    setItems((arr) => arr.filter((x) => x.id !== s.id));
                  }
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}